import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { supabase, supabaseConfigured, API_BASE } from "@/lib/supabase";
import { installApiErrorNormalizer } from "@/lib/apiError";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMe = useCallback(async (accessToken) => {
    if (!accessToken) {
      setMe(null);
      return null;
    }
    try {
      const { data } = await axios.get(`${API_BASE}/admin/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setMe(data);
      setError(null);
      return data;
    } catch (e) {
      setMe(null);
      setError(e?.response?.data?.detail || e.message);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!supabaseConfigured) {
      setLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      const s = data.session || null;
      if (mounted) setSession(s);
      if (s?.access_token) await loadMe(s.access_token);
      if (mounted) setLoading(false);
    })();
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, s) => {
      setSession(s || null);
      await loadMe(s?.access_token);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [loadMe]);

  const login = useCallback(async (email, password) => {
    if (!supabaseConfigured) throw new Error("Supabase niet geconfigureerd");
    const { data, error: err } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    if (err) throw err;
    setSession(data.session);
    await loadMe(data.session?.access_token);
    return data.session;
  }, [loadMe]);

  const logout = useCallback(async () => {
    if (supabaseConfigured) await supabase.auth.signOut();
    setSession(null);
    setMe(null);
  }, []);

  const authAxios = useMemo(() => {
    const instance = axios.create({ baseURL: API_BASE });
    instance.interceptors.request.use((cfg) => {
      if (session?.access_token) {
        cfg.headers.Authorization = `Bearer ${session.access_token}`;
      }
      return cfg;
    });
    installApiErrorNormalizer(instance);
    return instance;
  }, [session]);

  const value = { session, me, loading, error, login, logout, authAxios };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
